<template>
  <Card cardClasses="shadow-sm border-0">
    <div class="d-flex justify-content-between align-items-center mb-4">
      <h4 class="m-0 text-primary">
        <i class="bi bi-file-earmark-text me-2"></i>Z-Report Details
      </h4>
      <div class="d-flex gap-2">
        <button class="btn btn-outline-secondary" @click="goToZReports">
          <i class="bi bi-arrow-left me-1"></i> Back
        </button>
      </div>
    </div>

    <div class="container-fluid p-0">
      <div v-if="loading" class="text-center py-5">
        <Spinner spinner-class="mb-1" />
        <p class="text-muted mt-2">Loading Z-report...</p>
      </div>

      <div v-if="!loading" class="zreport-details-content fade-in" id="printable-report">
        <div class="text-center mb-4">
          <h1 class="report-title">{{ report.reference }}</h1>
          <p class="text-muted">Generated on {{ formatDate(report.generated_at) }}</p>
        </div>

        <div class="card border-0 shadow-sm mb-4">
          <div class="card-header bg-primary text-white py-3">
            <h5 class="mb-0"><i class="bi bi-info-circle me-2"></i>Report Information</h5>
          </div>
          <div class="card-body">
            <div class="row g-3">
              <div class="col-md-6 col-lg-3">
                <div class="info-group">
                  <h6 class="info-label">Report Reference</h6>
                  <p class="info-value">{{ report.reference }}</p>
                </div>
              </div>
              <div class="col-md-6 col-lg-3">
                <div class="info-group">
                  <h6 class="info-label">Shift Reference</h6>
                  <p class="info-value">{{ report.shift?.reference || 'N/A' }}</p>
                </div>
              </div>
              <div class="col-md-6 col-lg-3">
                <div class="info-group">
                  <h6 class="info-label">Store</h6>
                  <p class="info-value">{{ report.store?.name || 'N/A' }}</p>
                </div>
              </div>
              <div class="col-md-6 col-lg-3">
                <div class="info-group">
                  <h6 class="info-label">Operator</h6>
                  <p class="info-value">{{ report.user?.first_name }} {{ report.user?.last_name }}</p>
                </div>
              </div>
            </div>
          </div>
        </div>

        <div class="row mb-4">
          <div class="col-md-6 col-lg-3 mb-3">
            <div class="card border-0 shadow-sm h-100">
              <div class="card-body text-center">
                <i class="bi bi-receipt fs-1 text-primary mb-2"></i>
                <h3 class="mb-0">{{ report.total_transactions }}</h3>
                <p class="text-muted mb-0">Total Transactions</p>
              </div>
            </div>
          </div>
          <div class="col-md-6 col-lg-3 mb-3">
            <div class="card border-0 shadow-sm h-100">
              <div class="card-body text-center">
                <i class="bi bi-cash-stack fs-1 text-success mb-2"></i>
                <h3 class="mb-0">{{ formatNumber(report.total_sales) }}</h3>
                <p class="text-muted mb-0">Total Sales</p>
              </div>
            </div>
          </div>
          <div class="col-md-6 col-lg-3 mb-3">
            <div class="card border-0 shadow-sm h-100">
              <div class="card-body text-center">
                <i class="bi bi-percent fs-1 text-warning mb-2"></i>
                <h3 class="mb-0">{{ formatNumber(report.total_discount) }}</h3>
                <p class="text-muted mb-0">Total Discounts</p>
              </div>
            </div>
          </div>
          <div class="col-md-6 col-lg-3 mb-3">
            <div class="card border-0 shadow-sm h-100">
              <div class="card-body text-center">
                <i class="bi bi-receipt-cutoff fs-1 text-info mb-2"></i>
                <h3 class="mb-0">{{ formatNumber(report.total_tax) }}</h3>
                <p class="text-muted mb-0">Total Tax</p>
              </div>
            </div>
          </div>
        </div>

        <div class="card border-0 shadow-sm mb-4">
          <div class="card-header bg-success text-white py-3">
            <h5 class="mb-0"><i class="bi bi-credit-card me-2"></i>Payment Methods Breakdown</h5>
          </div>
          <div class="card-body">
            <div class="row g-3">
              <div class="col-md-4">
                <div class="payment-method-card">
                  <div class="d-flex align-items-center mb-2">
                    <i class="bi bi-cash fs-2 text-success me-3"></i>
                    <div>
                      <h6 class="mb-0 text-muted">Cash Payments</h6>
                      <h4 class="mb-0 text-success">{{ formatNumber(report.total_cash) }}</h4>
                    </div>
                  </div>
                </div>
              </div>
              <div class="col-md-4">
                <div class="payment-method-card">
                  <div class="d-flex align-items-center mb-2">
                    <i class="bi bi-phone fs-2 text-warning me-3"></i>
                    <div>
                      <h6 class="mb-0 text-muted">M-Pesa Payments</h6>
                      <h4 class="mb-0 text-warning">{{ formatNumber(report.total_mpesa) }}</h4>
                    </div>
                  </div>
                </div>
              </div>
              <div class="col-md-4">
                <div class="payment-method-card">
                  <div class="d-flex align-items-center mb-2">
                    <i class="bi bi-credit-card fs-2 text-primary me-3"></i>
                    <div>
                      <h6 class="mb-0 text-muted">PDQ Payments</h6>
                      <h4 class="mb-0 text-primary">{{ formatNumber(report.total_pdq) }}</h4>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        <div class="card border-0 shadow-sm mb-4">
          <div class="card-header bg-warning text-dark py-3">
            <h5 class="mb-0"><i class="bi bi-calculator me-2"></i>Cash Reconciliation</h5>
          </div>
          <div class="card-body">
            <div class="row g-3">
              <div class="col-md-6 col-lg-3">
                <div class="info-group">
                  <h6 class="info-label">Opening Cash</h6>
                  <p class="info-value text-success fw-bold">{{ formatNumber(report.opening_cash) }}</p>
                </div>
              </div>
              <div class="col-md-6 col-lg-3">
                <div class="info-group">
                  <h6 class="info-label">Closing Cash</h6>
                  <p class="info-value text-primary fw-bold">{{ formatNumber(report.closing_cash) }}</p>
                </div>
              </div>
              <div class="col-md-6 col-lg-3">
                <div class="info-group">
                  <h6 class="info-label">Expected Cash</h6>
                  <p class="info-value text-info fw-bold">{{ formatNumber(report.expected_cash) }}</p>
                </div>
              </div>
              <div class="col-md-6 col-lg-3">
                <div class="info-group">
                  <h6 class="info-label">Cash Variance</h6>
                  <p class="info-value" :class="getCashDifferenceClass(report.cash_difference)">
                    {{ 'KES ' + report.cash_difference }}
                  </p>
                </div>
              </div>
            </div>
            <div v-if="Math.abs(parseFloat(report.cash_difference)) > 100" class="alert alert-warning mt-3">
              <i class="bi bi-exclamation-triangle me-2"></i>
              <strong>Warning: </strong> Cash variance exceeds acceptable threshold (±100)
            </div>
          </div>
        </div>

        <div class="card border-0 shadow-sm mb-4">
          <div class="card-header bg-danger text-white py-3">
            <h5 class="mb-0"><i class="bi bi-arrow-return-left me-2"></i>Returns Information</h5>
          </div>
          <div class="card-body">
            <div class="row g-3">
              <div class="col-md-6">
                <div class="info-group">
                  <h6 class="info-label">Total Returns</h6>
                  <p class="info-value text-danger fw-bold">{{ report.total_returns }}</p>
                </div>
              </div>
              <div class="col-md-6">
                <div class="info-group">
                  <h6 class="info-label">Return Amount</h6>
                  <p class="info-value text-danger fw-bold">{{ formatNumber(report.total_return_amount) }}</p>
                </div>
              </div>
            </div>
          </div>
        </div>

        <div class="card border-0 shadow-sm mb-4">
          <div class="card-header bg-info text-white py-3">
            <h5 class="mb-0"><i class="bi bi-clock-history me-2"></i>Time Information</h5>
          </div>
          <div class="card-body">
            <div class="row g-3">
              <div class="col-md-4">
                <div class="info-group">
                  <h6 class="info-label">Shift Started</h6>
                  <p class="info-value">{{ report.shift_start }}</p>
                </div>
              </div>
              <div class="col-md-4">
                <div class="info-group">
                  <h6 class="info-label">Shift Ended</h6>
                  <p class="info-value">{{ report.shift_end }}</p>
                </div>
              </div>
              <div class="col-md-4">
                <div class="info-group">
                  <h6 class="info-label">Report Generated</h6>
                  <p class="info-value">{{ report.generated_at }}</p>
                </div>
              </div>
              <div class="col-md-12" v-if="report.notes">
                <div class="info-group">
                  <h6 class="info-label">Notes</h6>
                  <p class="info-value">{{ report.notes }}</p>
                </div>
              </div>
            </div>
          </div>
        </div>

        <div class="d-flex justify-content-center gap-2 mt-4">
          <button class="btn btn-primary" @click="viewShift(report.shift_id)">
            <i class="bi bi-eye me-1"></i> View Shift Details
          </button>
<!--          <button class="btn btn-outline-primary" @click="printReport">-->
<!--            <i class="bi bi-printer me-1"></i> Print Report-->
<!--          </button>-->
        </div>
      </div>
    </div>
  </Card>
</template>

<script>
import zreportService from "@/api/zreportService"
import router from "@/router"
import formatDate from "@/utils/formatDate"
import formatNumber from "@/utils/formatNumber"

export default {
  name: "ZReportDetails",

  data(){
    return {
      loading: true,
      report: {},
    }
  },

  methods:{
    formatDate,
    formatNumber,

    async fetchReport() {
      this.loading = true
      const id = this.$route.params.id
      this.report = await zreportService.getZReport(id)
      this.loading = false
    },

    goToZReports() {
      router.push('/z-reports')
    },

    viewShift(id) {
      router.push('/shift/' + id)
    },

    printReport() {
      window.print()
    },

    getCashDifferenceClass(difference) {
      const diff = parseFloat(difference)
      if (diff > 0) return 'text-success fw-bold'
      if (diff < 0) return 'text-danger fw-bold'
      return 'text-muted'
    },
  },

  async mounted() {
    await this.fetchReport()
  }
}
</script>

<style scoped>
.fade-in {
  animation: fadeIn 0.3s ease-in;
}

@keyframes fadeIn {
  from {
    opacity: 0;
  }
  to {
    opacity: 1;
  }
}

.report-title {
  font-size: 2.5rem;
  font-weight: 600;
  color: #0d4072;
}

.info-group {
  padding: 10px;
}

.info-label {
  font-size: 0.875rem;
  color: #6c757d;
  margin-bottom: 0.25rem;
  font-weight: 500;
}

.info-value {
  font-size: 1rem;
  color: #212529;
  margin-bottom: 0;
}

.payment-method-card {
  padding: 15px;
  border-radius: 8px;
  background-color: #f8f9fa;
}

@media print {
  .btn, .card-header {
    display: none !important;
  }
  
  .card {
    border: 1px solid #dee2e6 !important;
    box-shadow: none !important;
  }
}
</style>
